// Kho dự án (mock, lưu localStorage — sẽ thay bằng API khi có backend)
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import dayjs from 'dayjs';
import useWorkStore from '../WorkList/store';
import { memberName } from '../WorkList/data';
import { applyProjectAction, newProjectAct, seedProjects, type Phase, type ProjectAction, type ProjectInfo } from './data';

export const PROJECT_STORAGE_KEY = 'khothi-project-v1';

export type ProjectPatch = Partial<Pick<ProjectInfo, 'description' | 'startAt' | 'endAt' | 'progressMethod' | 'syncTaskDates' | 'taskWithinProject'>>;
export type MembersPatch = Pick<ProjectInfo, 'ownerIds' | 'memberIds' | 'followerIds'>;

interface ProjectState {
  projects: ProjectInfo[];
  act: (id: string, action: ProjectAction, by: string, note?: string) => string | null;
  update: (id: string, patch: ProjectPatch, by: string) => void;
  setMembers: (id: string, patch: MembersPatch, by: string) => void;
  setPhases: (id: string, phases: Phase[], by: string) => void;
  addLink: (id: string, name: string, url: string, by: string) => void;
  removeLink: (id: string, linkId: string) => void;
  comment: (id: string, text: string, by: string) => void;
}

const useProjectStore = create<ProjectState>()(persist((set, get) => {
  const patchOne = (id: string, fn: (p: ProjectInfo) => ProjectInfo) => set(s => ({ projects: s.projects.map(p => (p.id === id ? fn(p) : p)) }));
  return {
    projects: seedProjects(),

    act: (id, action, by, note) => {
      const p = get().projects.find(x => x.id === id);
      if (!p) return 'Không tìm thấy dự án';
      const next = applyProjectAction(p, action, useWorkStore.getState().tasks, { by, note });
      if (typeof next === 'string') return next;
      patchOne(id, () => next);
      return null;
    },

    update: (id, patch, by) => patchOne(id, p => {
      const activity = [...p.activity, newProjectAct(by, 'edit')];
      // Dời ngày bắt đầu → dời lịch các việc đang mở cùng số ngày
      const shift = p.syncTaskDates && patch.startAt && p.startAt ? dayjs(patch.startAt).diff(p.startAt, 'day') : 0;
      if (shift) {
        const moved = useWorkStore.getState().shiftTasks(id, shift, by);
        activity.push(newProjectAct(by, 'shift', `${shift > 0 ? '+' : ''}${shift} ngày · dời ${moved} công việc đang mở`));
      }
      return { ...p, ...patch, activity };
    }),

    setMembers: (id, patch, by) => patchOne(id, p => {
      const added = [...patch.ownerIds, ...patch.memberIds].filter(m => !p.ownerIds.includes(m) && !p.memberIds.includes(m));
      return { ...p, ...patch, activity: [...p.activity, newProjectAct(by, 'members', added.length ? `Thêm ${added.map(memberName).join(', ')}` : undefined)] };
    }),

    setPhases: (id, phases, by) => patchOne(id, p => ({ ...p, phases, activity: [...p.activity, newProjectAct(by, 'phase', phases.map(x => x.name).join(' → ') || undefined)] })),

    addLink: (id, name, url, by) => patchOne(id, p => ({
      ...p,
      links: [...p.links, { id: `ln-${Date.now().toString(36)}`, name, url, by, at: new Date().toISOString() }],
      activity: [...p.activity, newProjectAct(by, 'link', name)],
    })),

    removeLink: (id, linkId) => patchOne(id, p => ({ ...p, links: p.links.filter(l => l.id !== linkId) })),

    comment: (id, text, by) => {
      if (!text.trim()) return;
      patchOne(id, p => ({ ...p, activity: [...p.activity, newProjectAct(by, 'comment', text.trim())] }));
    },
  };
  // v2: dữ liệu mẫu theo phòng khám (thêm phòng chuyên môn, bỏ dự án Quận 10) — tạo lại từ seed
}, { name: PROJECT_STORAGE_KEY, version: 2, migrate: () => ({ projects: seedProjects() }) as ProjectState }));

export default useProjectStore;
